import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { JsonEditor } from './JsonEditor';
import { validateFormConfig } from './FormConfigValidator';
import { validateFormRules } from './FormRulesValidator';

// --- Helper function for syntax highlighting ---
function highlightJson(jsonStr: string): string {
    if (!jsonStr) return '';

    // Basic HTML escaping
    const escapedStr = jsonStr
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    return escapedStr.replace(
        /("(\u[a-zA-Z0-9]{4}|\\[^u]|[^\\]")*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
        function (match) {
            let cls = 'json-number';
            if (/^"/.test(match)) {
                cls = /:$/.test(match) ? 'json-key' : 'json-string';
            } else if (/true|false/.test(match)) {
                cls = 'json-boolean';
            } else if (/null/.test(match)) {
                cls = 'json-null';
            }
            return `<span class="${cls}">${match}</span>`;
        }
    );
}

// --- Custom JSON Editor Component ---
const CustomJsonEditor: React.FC<{
    value: string;
    onChange: (value: string) => void;
    readOnly: boolean;
}> = ({ value, onChange, readOnly }) => {
    const textRef = useRef<HTMLTextAreaElement>(null);
    const highlightRef = useRef<HTMLPreElement>(null);

    const handleScroll = () => {
        if (highlightRef.current && textRef.current) {
            highlightRef.current.scrollTop = textRef.current.scrollTop;
            highlightRef.current.scrollLeft = textRef.current.scrollLeft;
        }
    };

    return (
        <>
            <div className="json-editor-container">
                <textarea
                    ref={textRef}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onScroll={handleScroll}
                    className="json-editor-textarea blue-scrollbar"
                    readOnly={readOnly}
                    spellCheck="false"
                />
                <pre
                    ref={highlightRef}
                    className="json-editor-highlight blue-scrollbar"
                    dangerouslySetInnerHTML={{ __html: highlightJson(value) }}
                />
            </div>
        </>
    );
};

interface ConfigEditorProps {
    configType: "CONFIG" | "RULES" | "MAPPING";
    label: string;
    initialJson: string | undefined;
    onJsonChange: (jsonContent: string, isValid: boolean, errors: string[]) => void;
    isEditable?: boolean;
}

const ConfigEditor: React.FC<ConfigEditorProps> = ({
    configType,
    label,
    initialJson,
    onJsonChange,
    isEditable = true,
}) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [currentJson, setCurrentJson] = useState<string>("");
    const [isValidJson, setIsValidJson] = useState<boolean>(true);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    useEffect(() => {
        const initialValue = initialJson ?? "";
        setCurrentJson(initialValue);
        try {
            if (initialValue) JSON.parse(initialValue);
            setIsValidJson(true);
        } catch (e) {
            setIsValidJson(false);
        }
    }, [initialJson]);

  const handleJsonChange = useCallback((value: string) => {
    setCurrentJson(value);
    let isValid = true;
    let errors: string[] = [];

    try {
      if (value.trim() !== '') {
        const parsedJson = JSON.parse(value);
        
        // 2. Add a case for the 'RULES' type
        switch (configType) {
          case 'CONFIG': {
            const result = validateFormConfig(parsedJson);
            isValid = result.isValid;
            errors = result.errors;
            break;
          }
          case 'RULES': {
            const result = validateFormRules(parsedJson);
            isValid = result.isValid;
            errors = result.errors;
            break;
          }
          // You can add a 'MAPPING' case here later
          default:
            break;
        }
      }
    } catch (e) {
      isValid = false;
      errors = ['JSON format is invalid.'];
    }
    setIsValidJson(isValid);
    setValidationErrors(errors);
    onJsonChange(value, isValid, errors);
  }, [onJsonChange, configType]);

    const wrapperClassName = `config-editor-wrapper`;
    const handleToggleCollapse = () => {
        setIsCollapsed((prev) => !prev);
    };

    if (initialJson === undefined && !isEditable) {
        return (
            <div className={wrapperClassName}>
                <div
                    className="config-editor-header"
                    onClick={handleToggleCollapse}
                    style={{ opacity: 0.6 }}
                >
                    <h3>
                        {label} ({configType})
                    </h3>
                    <FontAwesomeIcon
                        className={`collapse-icon ${isCollapsed ? "collapsed" : ""}`}
                        icon={isCollapsed ? faChevronDown : faChevronUp}
                    />
                </div>
                <div className={`collapsible-content ${isCollapsed ? "collapsed" : ""}`}>
                    <p className="m-2 text-sm text-gray-500">
                        No {label} configuration found for this form.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={`max-h-[500px] ${wrapperClassName}`}>
            <div className="config-editor-header" onClick={handleToggleCollapse}>
                <h3>
                    {label} ({configType})
                </h3>
                <FontAwesomeIcon
                    className={`collapse-icon ${isCollapsed ? "collapsed" : ""}`}
                    icon={isCollapsed ? faChevronDown : faChevronUp} // Use appropriate icons
                />
            </div>

            <div className={`collapsible-content ${isCollapsed ? "collapsed" : ""}`}>
                <JsonEditor
                    value={currentJson}
                    onChange={handleJsonChange}
                    readOnly={!isEditable}
                />

                {!isValidJson && isEditable && validationErrors.length > 0 && (
                    <div className="validation-errors-container">
                        {validationErrors.map((error, index) => (
                            <p key={index} className="validation-error-item">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                                {error}
                            </p>
                        ))}
                    </div>
                )}
                {/* {!isValidJson && isEditable && (
          <p
            style={{
              color: "orange",
              fontSize: "10px",
              marginTop: "5px",
              flexShrink: 0,
            }}
          >
            JSON format is invalid.
          </p>
        )} */}
            </div>
        </div>
    );
};

export default ConfigEditor;